import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useToast } from 'context/ToastContext';
import Toast from './Toast';

/**
 * Container for toasts at specific position
 */
const Container = styled.div`
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;

  /* Top Right */
  &.top-right {
    top: 20px;
    right: 20px;
  }

  /* Top Center */
  &.top-center {
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
  }

  /* Bottom Right */
  &.bottom-right {
    bottom: 20px;
    right: 20px;
  }

  /* Bottom Center */
  &.bottom-center {
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
  }

  /* Mobile adjustments */
  @media (max-width: 640px) {
    &.top-right,
    &.bottom-right {
      right: 10px;
      left: 10px;
    }

    &.top-center,
    &.bottom-center {
      left: 10px;
      right: 10px;
      transform: none;
    }

    &.top-right,
    &.top-center {
      top: 10px;
    }

    &.bottom-right,
    &.bottom-center {
      bottom: 10px;
    }
  }
`;

/**
 * ToastContainer Component
 *
 * Renders all active toasts at their designated positions.
 * Automatically groups toasts by position.
 */
const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  // Group toasts by position
  const toastsByPosition = useMemo(() => {
    const grouped = {};
    toasts.forEach((toast) => {
      const position = toast.position || 'top-right';
      if (!grouped[position]) {
        grouped[position] = [];
      }
      grouped[position].push(toast);
    });
    return grouped;
  }, [toasts]);

  // Render a container for each position that has toasts
  return (
    <>
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <Container key={position} className={position}>
          {positionToasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              type={toast.type}
              title={toast.title}
              message={toast.message}
              position={position}
              dismissible={toast.dismissible}
              exiting={toast.exiting}
              action={toast.action}
              onDismiss={removeToast}
            />
          ))}
        </Container>
      ))}
    </>
  );
};

export default ToastContainer;
